import { useRef, useState } from "react";
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Avatar,
  Divider,
  Fade,
  useMediaQuery,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { alpha } from "@mui/material/styles";

export default function ProjectTypeSelector({
  currentStep,
  setCurrentStep,
  projectTypes,
  selectedService,
  handleServiceSelect,
}) {
  const [hovered, setHovered] = useState(null);
  const hoverTimeout = useRef(null);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const handleMouseEnter = (key) => {
    // clear any old timeout
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }
    // set new timeout
    hoverTimeout.current = setTimeout(() => {
      setHovered(key);
    }, 250); // delay in ms (adjust 200–300 for best UX)
  };

  const handleMouseLeave = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }
    setHovered(null);
  };

  if (currentStep !== 2) return null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: 0,
        width: "100%",
        height: "100%",
        position: "relative",
      }}
    >
      {/* Left side: Service list */}
      <Box
        sx={{
          width: isMobile ? "100%" : 400,
          overflow: "hidden",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          flexShrink: 0, // keep fixed width
          bgcolor: "background.paper",
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={2.5}
          borderBottom="1px solid #eee"
          bgcolor="grey.50"
        >
          <Typography variant="h6" fontWeight="700" color="text.primary">
            Select Project Type
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setCurrentStep(1)}
          >
            Back
          </Button>
        </Box>

        {/* Service list */}
        <Box p={2.5} height={500} overflow="auto">
          <Box display="flex" flexDirection="column" gap={2}>
            {projectTypes.map((type) => {
              const isSelected = selectedService === type.key;
              const isHovered = hovered === type.key;
              return (
                <Card
                  key={type.key}
                  elevation={isHovered ? 6 : 1}
                  sx={{
                    border: isSelected
                      ? "2px solid #4caf50"
                      : isHovered
                      ? "2px solid #2196f3"
                      : "1px solid #e0e0e0",
                    bgcolor: isSelected
                      ? "rgba(76,175,80,0.05)"
                      : "background.paper",
                    transform: isHovered ? "scale(1.03)" : "scale(1)",
                    opacity: hovered && !isHovered ? 0.6 : 1,
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    borderRadius: 2,
                  }}
                  onMouseEnter={() => handleMouseEnter(type.key)}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => handleServiceSelect(type.key, type.label)}
                >
                  <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        sx={{ bgcolor: "grey.100", color: "text.primary" }}
                      >
                        {type.icon}
                      </Avatar>
                      <Box flexGrow={1}>
                        <Typography variant="subtitle1" fontWeight="600">
                          {type.label}
                        </Typography>
                      </Box>
                      {isSelected && (
                        <CheckCircleIcon
                          color="success"
                          sx={{ fontSize: 22 }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Right side: Preview panel */}

      {hovered && (
        <Fade in={!!hovered} timeout={400}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              borderRadius: 0,
              transition: "all 0.3s ease",
              boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
              width: "100%",
              height: "100%",
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.9),
              width: "300px",
              minWidth: "300px",
              position: "absolute",
              left: isMobile ? 40 : 400,
              top: isMobile ? 590 : 0,
              zIndex: "9999",
            }}
          >
            <Box mb={2}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1446776877081-d282a0f896e2?w=300&h=180&fit=crop"
                alt="Service preview"
                sx={{
                  width: "100%",
                  height: 140,
                  objectFit: "cover",
                  borderRadius: 1.5,
                }}
              />
            </Box>
            <Typography
              variant="subtitle2"
              fontWeight="700"
              color="text.primary"
              mb={0.5}
            >
              {projectTypes.find((t) => t.key === hovered)?.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {projectTypes.find((t) => t.key === hovered)?.description}
            </Typography>
          </Paper>
        </Fade>
      )}
    </div>
  );
}
